import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "../hooks/useTranslation";
import { useWallet } from "../hooks/useWallet";
import { API_BASE } from "../constants/index";
import { toast } from "react-hot-toast";
import { FiChevronLeft, FiChevronRight, FiRotateCcw, FiClock, FiFilter, FiCalendar, FiBarChart2, FiCheckCircle } from "react-icons/fi";
import dayjs from "dayjs";

const GET_ROI_BONUS_HISTORY = `${API_BASE}/publicApi/getRoiBonusHistory`;
const PAGE_LIMIT = 10;

export default function IncomeReport() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { account, isConnected, isCorrectNetwork } = useWallet();

    const [records, setRecords] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    // Filter state
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [timeFilter, setTimeFilter] = useState("All");
    const [levelFilter, setLevelFilter] = useState("All");

    const getRoiBonusHistory = useCallback(async (page = 1) => {
        if (!account || !isConnected || !isCorrectNetwork) return;

        setIsLoading(true);
        try {
            const res = await fetch(GET_ROI_BONUS_HISTORY, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ walletAddress: account, page, limit: PAGE_LIMIT }),
            });

            const json = await res.json().catch(() => ({}));

            if (!res.ok || !json?.success) {
                toast.error(json?.message || "Failed to get ROI bonus history");
                return;
            }

            setRecords(json.data || []);
            setTotalPages(json.total_pages || 1);
            setTotalRecords(json.total || 0);
            setCurrentPage(json.page || page);
        } catch (error) {
            console.error("Error getting ROI bonus history:", error);
            toast.error("Failed to load history");
        } finally {
            setIsLoading(false);
        }
    }, [account, isConnected, isCorrectNetwork]);

    useEffect(() => {
        getRoiBonusHistory(1);
    }, [getRoiBonusHistory]);

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages || isLoading) return;
        setCurrentPage(page);
        getRoiBonusHistory(page);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const formatAmount = (val) => {
        const n = parseFloat(val);
        if (isNaN(n)) return "0.0000";
        return n.toFixed(4);
    };

    // Pagination page numbers with ellipsis
    const getPageNumbers = () => {
        const pages = [];
        if (totalPages <= 5) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1);
            if (currentPage > 3) pages.push("...");
            for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                pages.push(i);
            }
            if (currentPage < totalPages - 2) pages.push("...");
            pages.push(totalPages);
        }
        return pages;
    };

    return (
        <div className="relative overflow-x-hidden -mx-4 -my-8 min-h-[calc(100vh-200px)]">
            <div className="relative z-10 pt-6 px-4 max-w-md mx-auto">
                {/* <div className="flex items-center gap-3 mb-6">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="flex-shrink-0 size-10 rounded-full flex items-center justify-center text-white hover:bg-white/10 transition-colors"
                        aria-label={t("common.back") || "Back"}
                    >
                        <span className="material-symbols-outlined text-2xl">arrow_back</span>
                    </button>
                    <h1 className="text-xl font-semibold text-white truncate">
                        {t("incomeReport.title") || "Income Report"}
                    </h1>
                </div>

                <div className="flex items-center text-[17px] font-bold mb-6">
                    <span className="text-[#8b5cf6] mr-2">Total Bonus :</span>
                    <span className="text-white tracking-wide">1,345 VUSDT</span>
                </div>

                <div className="bg-[#05000a] border border-[#231735] rounded-xl overflow-hidden shadow-lg mb-8">

                    <div className="flex justify-between items-center px-5 py-4 border-b border-[#231735]">
                        <div className="flex items-center text-white">
                            <FiClock className="mr-3" size={20} />
                            <h2 className="text-lg font-semibold tracking-wide">Bonus History</h2>
                        </div>
                        <button
                            onClick={() => setIsFilterOpen(true)}
                            className="text-gray-300 hover:text-white transition-colors"
                        >
                            <FiFilter size={20} />
                        </button>
                    </div>

                    <div className="grid grid-cols-4 p-3 bg-[#0a0512] text-white text-xs font-semibold border-b border-[#231735]">
                        <div className="col-span-1 text-left pl-1">Date & Time</div>
                        <div className="col-span-1 text-center">Buyer</div>
                        <div className="col-span-1 text-center">Amount</div>
                        <div className="col-span-1 text-right pr-1">Level</div>
                    </div>

                    <div className="flex flex-col text-sm text-gray-200">

                        <div className="grid grid-cols-4 px-3 py-4 border-b border-[#1a1226] items-center">
                            <div className="col-span-1 text-[11px] leading-tight text-left text-gray-300 pl-1">
                                2026-05-25<br />15:30
                            </div>
                            <div className="col-span-1 text-center text-[12px] truncate px-1">0xA12fy...887e</div>
                            <div className="col-span-1 text-center text-[12px]">
                                <div className="text-white">+12.50</div>
                                <div>USDT</div>
                            </div>
                            <div className="col-span-1 text-right text-[13px] pr-2">P3</div>
                        </div>

                        <div className="grid grid-cols-4 px-3 py-4 border-b border-[#1a1226] items-center">
                            <div className="col-span-1 text-[11px] leading-tight text-left text-gray-300 pl-1">
                                2026-05-24<br />19:42
                            </div>
                            <div className="col-span-1 text-center text-[12px] truncate px-1">0xA12fy...887e</div>
                            <div className="col-span-1 text-center text-[12px]">
                                <div className="text-white">+5.00</div>
                                <div>USDT</div>
                            </div>
                            <div className="col-span-1 text-right text-[13px] pr-2">P7</div>
                        </div>

                        <div className="grid grid-cols-4 px-3 py-4 items-center">
                            <div className="col-span-1 text-[11px] leading-tight text-left text-gray-300 pl-1">
                                2026-05-24<br />21:48
                            </div>
                            <div className="col-span-1 text-center text-[12px] truncate px-1">0xA12fy...887e</div>
                            <div className="col-span-1 text-center text-[12px]">
                                <div className="text-white">+12.50</div>
                                <div>USDT</div>
                            </div>
                            <div className="col-span-1 text-right text-[13px] pr-2">P1</div>
                        </div>
                    </div>
                </div> */}
            </div>

            <div className="relative z-10 pb-8 px-4 max-w-md mx-auto">

                {/* ── Header ── */}
                <div className="flex items-center gap-3 mb-6">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="flex-shrink-0 size-10 rounded-full flex items-center justify-center text-white hover:bg-white/10 transition-colors"
                        aria-label={t("common.back") || "Back"}
                    >
                        <span className="material-symbols-outlined text-2xl">arrow_back</span>
                    </button>
                    <div className="flex-1 min-w-0">
                        <h1 className="text-xl font-semibold text-white truncate">
                            {t("incomeReport.title") || "Income Report"}
                        </h1>
                        <p className="text-sm text-[#E8DEF8] truncate">
                            {totalRecords > 0
                                ? `${totalRecords} ${t("incomeReport.totalRecords") || "records"}`
                                : t("incomeReport.subtitle") || "Your daily ROI breakdown"}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => getRoiBonusHistory(currentPage)}
                        disabled={isLoading}
                        className="flex-shrink-0 size-10 rounded-full flex items-center justify-center text-[#DFD2FE] hover:bg-[#9770DD]/20 transition-colors disabled:opacity-50"
                        aria-label="Refresh"
                    >
                        <FiRotateCcw className={`size-5 ${isLoading ? "animate-spin" : ""}`} />
                    </button>
                </div>

                {/* ── Loading Skeleton ── */}
                <AnimatePresence mode="wait">
                    {isLoading ? (
                        <motion.div
                            key="loading"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="space-y-3"
                        >
                            {Array.from({ length: 5 }).map((_, i) => (
                                <div
                                    key={i}
                                    className="rounded-2xl bg-[#1a1023]/60 border border-[#9770DD]/15 p-4 animate-pulse"
                                >
                                    <div className="h-4 bg-[#9770DD]/20 rounded w-1/3 mb-3" />
                                    <div className="grid grid-cols-3 gap-3">
                                        <div className="h-8 bg-[#9770DD]/10 rounded" />
                                        <div className="h-8 bg-[#9770DD]/10 rounded" />
                                        <div className="h-8 bg-[#9770DD]/10 rounded" />
                                    </div>
                                </div>
                            ))}
                        </motion.div>
                    ) : records.length === 0 ? (
                        /* ── Empty State ── */
                        <motion.div
                            key="empty"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            className="flex flex-col items-center justify-center py-20 text-center"
                        >
                            <div className="size-16 rounded-full bg-[#9770DD]/15 flex items-center justify-center mb-4">
                                <span className="material-symbols-outlined text-3xl text-[#9770DD]">history</span>
                            </div>
                            <p className="text-white font-semibold text-lg mb-1">
                                {t("incomeReport.noHistory") || "No Records Yet"}
                            </p>
                            <p className="text-[#ad90cb] text-sm">
                                {t("incomeReport.noHistoryDesc") || "Your daily ROI bonus history will appear here."}
                            </p>
                        </motion.div>
                    ) : (
                        /* ── Record List ── */
                        <motion.div
                            key="list"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="space-y-3"
                        >
                            {records.map((record, idx) => (
                                <motion.div
                                    key={`${record.urc_cdate}-${idx}`}
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: idx * 0.04, duration: 0.25 }}
                                    className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#1e1228] via-[#1a1023] to-[#150a1c] border border-[#9770DD]/30 p-4 shadow-lg shadow-[#9770DD]/10"
                                >
                                    {/* Top shimmer line */}
                                    <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#9770DD]/50 to-transparent" />

                                    {/* Date */}
                                    <div className="flex items-center gap-2 mb-3">
                                        <span className="material-symbols-outlined text-[#9770DD] text-base">calendar_today</span>
                                        <span className="text-xs font-semibold text-[#DFD2FE] tracking-wide">
                                            {dayjs(record.urc_cdate).format("DD MMM YYYY, HH:mm")}
                                        </span>
                                    </div>

                                    {/* Stats grid */}
                                    <div className="grid grid-cols-3 gap-2">
                                        {/* Total Daily ROI */}
                                        <div className="rounded-xl bg-[#241630]/60 border border-[#9770DD]/20 px-3 py-2 flex flex-col gap-1">
                                            <span className="text-[10px] font-medium text-[#ad90cb] uppercase tracking-wider leading-tight">
                                                {t("incomeReport.totalDailyRoi") || "Total ROI"}
                                            </span>
                                            <span className="text-[#DFD2FE] font-bold text-sm tabular-nums">
                                                {formatAmount(record.total_daily_roi)}
                                            </span>
                                        </div>

                                        {/* Daily Community */}
                                        <div className="rounded-xl bg-[#241630]/60 border border-[#9770DD]/20 px-3 py-2 flex flex-col gap-1">
                                            <span className="text-[10px] font-medium text-[#ad90cb] uppercase tracking-wider leading-tight">
                                                {t("incomeReport.dailyCommunity") || "Daily Community"}
                                            </span>
                                            <span className="text-[#DFD2FE] font-bold text-sm tabular-nums">
                                                {formatAmount(record.daily_community)}
                                            </span>
                                        </div>

                                        {/* Daily Generation */}
                                        <div className="rounded-xl bg-[#241630]/60 border border-[#9770DD]/20 px-3 py-2 flex flex-col gap-1">
                                            <span className="text-[10px] font-medium text-[#ad90cb] uppercase tracking-wider leading-tight">
                                                {t("incomeReport.dailyGeneration") || "Daily Generation"}
                                            </span>
                                            <span className="text-[#DFD2FE] font-bold text-sm tabular-nums">
                                                {formatAmount(record.daily_generation)}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Glow */}
                                    <div className="absolute right-3 top-3 size-10 rounded-full bg-[#9770DD]/10 blur-md pointer-events-none" />
                                </motion.div>
                            ))}

                            {/* ── Pagination ── */}
                            {totalPages > 1 && (
                                <div className="flex items-center justify-center gap-1 pt-4">
                                    {/* Prev */}
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1 || isLoading}
                                        className="size-9 rounded-xl flex items-center justify-center text-[#DFD2FE] bg-[#1a1023]/80 border border-[#9770DD]/20 hover:border-[#9770DD]/50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                                    >
                                        <FiChevronLeft className="size-4" />
                                    </button>

                                    {getPageNumbers().map((p, i) =>
                                        p === "..." ? (
                                            <span key={`ellipsis-${i}`} className="size-9 flex items-center justify-center text-[#ad90cb] text-sm">
                                                ...
                                            </span>
                                        ) : (
                                            <button
                                                key={p}
                                                onClick={() => handlePageChange(p)}
                                                disabled={isLoading}
                                                className={`size-9 rounded-xl text-sm font-semibold transition-all disabled:cursor-not-allowed ${p === currentPage
                                                    ? "bg-[#9770DD] text-white shadow-lg shadow-[#9770DD]/40"
                                                    : "text-[#DFD2FE] bg-[#1a1023]/80 border border-[#9770DD]/20 hover:border-[#9770DD]/50"
                                                    }`}
                                            >
                                                {p}
                                            </button>
                                        )
                                    )}

                                    {/* Next */}
                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages || isLoading}
                                        className="size-9 rounded-xl flex items-center justify-center text-[#DFD2FE] bg-[#1a1023]/80 border border-[#9770DD]/20 hover:border-[#9770DD]/50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                                    >
                                        <FiChevronRight className="size-4" />
                                    </button>
                                </div>
                            )}

                            {/* Page info */}
                            {totalPages > 1 && (
                                <p className="text-center text-xs text-[#ad90cb] pb-2">
                                    {t("incomeReport.page") || "Page"} {currentPage} / {totalPages}
                                </p>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Filter Bottom Sheet */}
            <AnimatePresence>
                {isFilterOpen && (
                    <>
                        {/* Overlay */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-gray-950/60 z-[60]"
                            onClick={() => setIsFilterOpen(false)}
                        />
                        {/* Bottom Sheet */}
                        <motion.div
                            initial={{ y: "100%" }}
                            animate={{ y: 0 }}
                            exit={{ y: "100%" }}
                            transition={{ type: "spring", damping: 25, stiffness: 300 }}
                            className="fixed bottom-0 left-0 right-0 max-w-md mx-auto bg-[#05000a] border-t border-[#231735] rounded-t-3xl z-[70] pb-8 pt-6 shadow-2xl"
                        >
                            {/* Header */}
                            <div className="px-6 flex items-center mb-8">
                                <FiFilter className="text-gray-400 mr-3" size={20} />
                                <h2 className="text-[17px] font-semibold text-white tracking-wide">Filter</h2>
                            </div>

                            <div className="px-6 space-y-8 max-h-[60vh] overflow-y-auto">
                                {/* Time Range */}
                                <div>
                                    <div className="flex items-center text-[13px] text-gray-400 mb-4">
                                        <FiCalendar className="mr-2" size={15} />
                                        <span>Time Range</span>
                                    </div>
                                    <div className="grid grid-cols-3 gap-3">
                                        {['All', 'Today', '1W', '1M', '3M'].map((tRange) => (
                                            <button
                                                key={tRange}
                                                onClick={() => setTimeFilter(tRange)}
                                                className={`py-2 rounded-xl text-sm font-medium transition-colors border ${timeFilter === tRange
                                                    ? 'bg-[#1a0b2e] border-[#5a2e98] text-white'
                                                    : 'bg-[#0a0512] border-[#231735] text-gray-300 hover:border-gray-600'
                                                    }`}
                                            >
                                                {tRange}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Level */}
                                <div>
                                    <div className="flex items-center text-[13px] text-gray-400 mb-4">
                                        <FiBarChart2 className="mr-2" size={15} />
                                        <span>Level</span>
                                    </div>
                                    <div className="grid grid-cols-4 gap-3">
                                        {['All', 'L0', 'L1', 'L2', 'L3', 'L4', 'L5', 'L6', 'L7', 'L8', 'L9', 'L10'].map((lvl) => (
                                            <button
                                                key={lvl}
                                                onClick={() => setLevelFilter(lvl)}
                                                className={`py-2 rounded-xl text-sm font-medium transition-colors border ${levelFilter === lvl
                                                    ? 'bg-[#1a0b2e] border-[#5a2e98] text-white'
                                                    : 'bg-[#0a0512] border-[#231735] text-gray-300 hover:border-gray-600'
                                                    }`}
                                            >
                                                {lvl}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="px-6 pt-8 flex gap-4 mt-2">
                                <button
                                    onClick={() => {
                                        setTimeFilter('All');
                                        setLevelFilter('All');
                                    }}
                                    className="flex-1 py-3.5 rounded-xl bg-[#11071d] hover:bg-[#1a0b2e] border border-[#2a1b3d] text-white text-[15px] font-medium flex items-center justify-center transition-colors"
                                >
                                    <FiRotateCcw className="mr-2" size={16} />
                                    Reset
                                </button>
                                <button
                                    onClick={() => setIsFilterOpen(false)}
                                    className="flex-1 py-3.5 rounded-xl bg-[#0a0512] border border-[#231735] hover:bg-[#11071d] text-white text-[15px] font-medium flex items-center justify-center transition-colors"
                                >
                                    <FiCheckCircle className="mr-2" size={16} />
                                    Done
                                </button>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
}
